import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useTranslation } from "react-i18next";
import styles from "./StrokeReport.module.css";
import { jsPDF } from 'jspdf'; 
import html2canvas from 'html2canvas';

interface RiskFactor {
  keyword: string;
  description: string;
}

interface ExpandedRiskFactor extends RiskFactor {
  categoryKey: string;
  subCategoryKey: string;
  priority: number;
}

interface SharedResult {
  kind: string;
  createdAt?: any;
  score: number;
  riskFactors: RiskFactor[];
  qaRiskFactors?: RiskFactor[];
}

const getCategory = (keyword: string): string => {
  if (
    keyword.includes("혈압") ||
    keyword.includes("당뇨") ||
    keyword.includes("심방세동") ||
    keyword.includes("콜레스테롤") ||
    keyword.includes("가족력")
  ) {
    return "category_medical_history"; 
  }
  return "category_basic"; 
};

const getSubCategory = (keyword: string): string => {
  if (keyword.includes("혈압")) return "고혈압";
  if (keyword.includes("당뇨")) return "당뇨";
  if (keyword.includes("심방세동")) return "심방세동";
  if (keyword.includes("콜레스테롤")) return "고지혈증";
  if (keyword.includes("가족력")) return "가족력";
  if (keyword.includes("음식") || keyword.includes("음료")) return "식습관";
  if (keyword.includes("운동") || keyword.includes("신체")) return "운동";
  if (keyword.includes("흡연") || keyword.includes("담배")) return "흡연";
  if (keyword.includes("음주") || keyword.includes("술")) return "음주";
  return "기타"; 
};

const getCategoryPriority = (categoryKey: string): number => {
  if (categoryKey === "category_basic") return 1;
  if (categoryKey === "category_medical_history") return 2;
  return 99;
};

const StrokeReport: React.FC = () => {
  const { id, hospitalId } = useParams<{ id: string; hospitalId?: string }>();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const { t } = useTranslation();

  const reportContentRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const navigate = useNavigate();
  const [data, setData] = useState<SharedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const originalRootOverflow = root ? root.style.overflow : '';

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    if (root) root.style.overflow = 'auto';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      if (root) root.style.overflow = originalRootOverflow;
    };
  }, []);

  useEffect(() => {
    const fetchResult = async () => {
      if (!id) {
        setError("잘못된 접근입니다.");
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, "sharedStrokeResults", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setData(snap.data() as SharedResult);
        } else {
          setError("결과를 찾을 수 없습니다.");
        }
      } catch (err) {
        console.error("Firestore getDoc error:", err);
        setError("결과 불러오기에 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [id]);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("ko-KR", {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(/\. /g, '.').slice(0, -1); 
  };

  const handleDownloadPdf = async () => {
    const container = reportContentRef.current;
    
    if (!container) {
        alert("오류: 리포트 화면을 찾을 수 없습니다.");
        return;
    }

    setIsGeneratingPdf(true);
    
    const originalWidth = container.style.width;
    
    try {
      container.style.width = '720px'; 
      
      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2); 

      const elements = Array.from(container.querySelectorAll('[data-pdf-section="true"]'));

      let currentHeight = 0; 
      let currentPage = 1; 

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        } as any);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (currentHeight + imgHeight > contentHeight) {
          pdf.addPage(); 
          currentPage++;
          currentHeight = 0;
        }

        pdf.addImage(imgData, 'JPEG', margin, margin + currentHeight, contentWidth, imgHeight);
        currentHeight += imgHeight + 2;
      }
      
      const footer = container.querySelector('[data-pdf-footer="true"]') as HTMLElement;
      if (footer) {
         const footerCanvas = await html2canvas(footer, { scale: 2, backgroundColor: '#ffffff' } as any);
         const footerImg = footerCanvas.toDataURL('image/jpeg', 0.95);
         const footerHeight = (footerCanvas.height * contentWidth) / footerCanvas.width;

         if (currentHeight + footerHeight > contentHeight) {
            pdf.addPage();
            currentPage++; 
            currentHeight = 0;
         }
         pdf.addImage(footerImg, 'JPEG', margin, margin + currentHeight, contentWidth, footerHeight);
      }

      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text(`${i} / ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save('stroke-report.pdf');

    } catch (error: any) {
      console.error("PDF Error:", error);
      alert(`저장 실패: ${error.message || error}`);
    } finally {
      setIsGeneratingPdf(false);
      if (container) {
        container.style.width = originalWidth; 
      }
    }
  };

  const subGroupedFactors = useMemo(() => {
    const factorsToUse = data?.qaRiskFactors || data?.riskFactors;
    if (!factorsToUse) return new Map<string, Map<string, ExpandedRiskFactor[]>>();

    const factorsWithCategory = factorsToUse.map(factor => {
      const categoryKey = getCategory(factor.keyword);
      const subCategoryKey = getSubCategory(factor.keyword);
      const priority = getCategoryPriority(categoryKey);
      return { ...factor, categoryKey, subCategoryKey, priority };
    });

    factorsWithCategory.sort((a, b) => a.priority - b.priority);

    const grouped = new Map<string, ExpandedRiskFactor[]>();
    factorsWithCategory.forEach(factor => {
      if (!grouped.has(factor.categoryKey)) {
        grouped.set(factor.categoryKey, []);
      }
      grouped.get(factor.categoryKey)?.push(factor);
    });

    const categoryMap = new Map<string, Map<string, ExpandedRiskFactor[]>>();
    grouped.forEach((factors, categoryKey) => {
      const subGroup = new Map<string, ExpandedRiskFactor[]>();
      factors.forEach(factor => {
        const subKey = factor.subCategoryKey; 
        if (!subGroup.has(subKey)) {
          subGroup.set(subKey, []);
        }
        subGroup.get(subKey)?.push(factor);
      });
      categoryMap.set(categoryKey, subGroup);
    });
    return categoryMap;
  }, [data]);

  if (loading) return <div className={styles.loading}>⏳ {t("loading") || "결과를 불러오는 중..."}</div>;
  if (error) return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => navigate(`${prefix}/`)}>{t("back_to_home") || "홈으로"}</button>
      </div>
    );
  if (!data) return null;

  const categoryRiskCounts = new Map<string, number>();
  subGroupedFactors.forEach((subGroups, categoryKey) => {
    let count = 0;
    // subGroups는 Map<string, ExpandedRiskFactor[]> 타입입니다.
    subGroups.forEach((factors: ExpandedRiskFactor[]) => {
      count += factors.length;
    });
    categoryRiskCounts.set(categoryKey, count);
  });

  // 헬퍼: 카테고리 렌더링 함수
  const renderCategoryGroupContent = (
    subGroups: Map<string, ExpandedRiskFactor[]>,
    categoryKey: string,
    riskCount: number
  ) => (
    <React.Fragment>
      <div className={styles.sectionHeader} data-pdf-section="true">
        <h3 className={styles.categoryTitle}>
          {categoryKey === 'category_basic' 
            ? t('stroke_report.category_basic_title') 
            : t('stroke_report.category_history_title')}
        </h3>

        <span className={styles.riskBadge}>
          {t('risk_count_badge', '{{count}}개의 위험요소 식별', { count: riskCount })}
        </span>
      </div>

      <p className={styles.sectionDescription} data-pdf-section="true">
        {categoryKey === 'category_basic' 
          ? t('stroke_report.category_basic_desc') 
          : t('stroke_report.category_history_desc')}
      </p>
      
      {Array.from(subGroups.entries()).map(([subCategoryKey, factors]) => (
        <div key={subCategoryKey} className={styles.subCategoryGroup}>
          <h4 className={styles.subCategoryTitle} data-pdf-section="true">{subCategoryKey}</h4>
          
          <div className={styles.qaList}>
            {factors.length > 0 ? (
              factors.map((factor, idx) => (
                <div key={idx} className={styles.qaItem} data-pdf-section="true">
                  <span className={styles.qaQuestion}>{factor.keyword}</span>
                  <span className={styles.qaAnswer}>{factor.description}</span>
                </div>
              ))
            ) : (
              <p className={styles.noRiskMessage} data-pdf-section="true">{t("no_risk_factor", "식별된 위험인자 없음")}</p>
            )}
          </div>
        </div>
      ))}
    </React.Fragment>
  );

  return (
    <div className={styles.reportWrapper}>
      <div className={styles.reportContainer} ref={reportContentRef}>
        
        <header className={styles.header} data-pdf-section="true">
          <div className={styles.logo}>
            <span style={{ backgroundColor: "#F9A825" }}></span>
            <span style={{ backgroundColor: "#1E88E5" }}></span>
            <span style={{ backgroundColor: "#E53935" }}></span>
            <span style={{ backgroundColor: "#43A047" }}></span>
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.mainTitle}>BrainCheck</h1>
            <h2 className={styles.subTitle}>뇌졸중 위험요소 검사 결과</h2>
            <div className={styles.metaInfo}>
              <span>{`검사 일자 ${formatDate(data.createdAt)}`}</span>
              <span>v1.0.0</span>
              <span>{`UID ${id?.substring(0, 12) || 'N/A'}`}</span>
            </div>
          </div>
        </header>

        <section className={styles.contentSection}>
          {Array.from(subGroupedFactors.entries()).map(([categoryKey, subGroups], index) => (
            <React.Fragment key={categoryKey}>
              {index > 0 && (
                <div style={{ height: '30px', visibility: 'hidden' }} data-pdf-section="true"></div>
              )}

              <div className={styles.categoryGroup}>
                {renderCategoryGroupContent(subGroups, categoryKey, categoryRiskCounts.get(categoryKey) || 0)}
              </div>
            </React.Fragment>
          ))}

          {subGroupedFactors.size === 0 && (
             <div className={styles.categoryGroup} data-pdf-section="true">
               <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                 {t("no_risk_total", "검사 결과, 특별히 식별된 뇌졸중 위험 요인이 없습니다. 건강한 생활 습관을 유지하세요!")}
               </p>
             </div>
          )}
        </section>

        <div className={styles.footerInfo} data-pdf-footer="true" style={{ marginTop: '40px', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
             BrainCheck © 2025 SHMD. All rights reserved.
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '20px' }}>
        <button 
          onClick={handleDownloadPdf} 
          className={styles.printButton}
          disabled={isGeneratingPdf}
          style={{ position: 'relative', zIndex: 9999, cursor: 'pointer' }} 
        >
          {isGeneratingPdf ? "PDF 생성 중..." : "📄 PDF로 저장 / 인쇄"}
        </button>
      </div>

    </div>
  );
};

export default StrokeReport;